import { useState, useMemo } from 'react'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'

interface PaisItem {
  id: string
  pais: string
  total: number
}

interface EspecialidadItem {
  especialidad: string
  total: number
  porcentaje: number
}

interface MedicoRaw {
  pais: string
  especialidad: string
}

interface OpellaNoValginaData {
  titulo: string
  subtitulo: string
  total_medicos: number
  total_especialidades: number
  paises: PaisItem[]
  especialidades: EspecialidadItem[]
  todos_medicos: MedicoRaw[]
}

export function OpellaNoValginaPage() {
  const { data, isLoading, error } = useDashReport<OpellaNoValginaData>('opella/novalgina')
  const [filtroPais, setFiltroPais] = useState('todos')
  const [filtroEspecialidad, setFiltroEspecialidad] = useState('todas')
  const [paisesExpanded, setPaisesExpanded] = useState(false)
  const [especialidadesExpanded, setEspecialidadesExpanded] = useState(false)

  // Compute filtered data
  const computed = useMemo(() => {
    if (!data) return null

    const paisSeleccionado = filtroPais === 'todos'
      ? null
      : data.paises.find(p => String(p.id) === filtroPais)?.pais ?? null

    let medicosFiltrados = data.todos_medicos

    if (paisSeleccionado) {
      medicosFiltrados = medicosFiltrados.filter(m => m.pais === paisSeleccionado)
    }

    const especialidadSeleccionada = filtroEspecialidad === 'todas' ? null : filtroEspecialidad

    if (especialidadSeleccionada) {
      medicosFiltrados = medicosFiltrados.filter(m => m.especialidad === especialidadSeleccionada)
    }

    const totalMedicosFiltrados = medicosFiltrados.length

    // Titulo resumen
    let tituloResumen = 'Resumen General'
    if (paisSeleccionado && especialidadSeleccionada) {
      tituloResumen = `${paisSeleccionado} - ${especialidadSeleccionada}`
    } else if (paisSeleccionado) {
      tituloResumen = paisSeleccionado
    } else if (especialidadSeleccionada) {
      tituloResumen = especialidadSeleccionada
    }

    // Tabla paises
    const paisesMap: Record<string, number> = {}
    medicosFiltrados.forEach(m => {
      paisesMap[m.pais] = (paisesMap[m.pais] || 0) + 1
    })
    const paisesArray = Object.entries(paisesMap)
      .map(([pais, total]) => ({
        pais,
        total,
        porcentaje: totalMedicosFiltrados > 0 ? ((total / totalMedicosFiltrados) * 100).toFixed(2) : '0',
      }))
      .sort((a, b) => b.total - a.total)

    // Tabla especialidades
    const espMap: Record<string, number> = {}
    medicosFiltrados.forEach(m => {
      espMap[m.especialidad] = (espMap[m.especialidad] || 0) + 1
    })
    const especialidadesArray = Object.entries(espMap)
      .map(([especialidad, total]) => ({
        especialidad,
        total,
        porcentaje: totalMedicosFiltrados > 0 ? ((total / totalMedicosFiltrados) * 100).toFixed(2) : '0',
      }))
      .sort((a, b) => b.total - a.total)

    // Especialidades disponibles para filtro (depende del pais seleccionado)
    let medicosPorPais = data.todos_medicos
    if (paisSeleccionado) {
      medicosPorPais = medicosPorPais.filter(m => m.pais === paisSeleccionado)
    }
    const espDisponiblesMap: Record<string, number> = {}
    medicosPorPais.forEach(m => {
      espDisponiblesMap[m.especialidad] = (espDisponiblesMap[m.especialidad] || 0) + 1
    })
    const espDisponibles = Object.entries(espDisponiblesMap)
      .map(([especialidad, total]) => ({ especialidad, total }))
      .sort((a, b) => b.total - a.total)

    return {
      totalMedicos: totalMedicosFiltrados,
      totalPaises: paisesArray.length,
      totalEspecialidades: especialidadesArray.length,
      tituloResumen,
      paisesArray,
      especialidadesArray,
      espDisponibles,
    }
  }, [data, filtroPais, filtroEspecialidad])

  const hayFiltrosActivos = filtroPais !== 'todos' || filtroEspecialidad !== 'todas'

  function handleFiltroPais(value: string) {
    setFiltroPais(value)
    setFiltroEspecialidad('todas')
  }

  function limpiarFiltros() {
    setFiltroPais('todos')
    setFiltroEspecialidad('todas')
  }

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data || !computed) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">{data.titulo}</h1>
                <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="https://enterprise.tonalstrategies.com/pricing/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
              >
                Calculador
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center">
              <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filtros
            </h3>
            {hayFiltrosActivos && (
              <button
                onClick={limpiarFiltros}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Limpiar filtros
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Filtro de País */}
            <div>
              <label htmlFor="select-pais" className="flex items-center text-sm font-semibold text-gray-900 mb-2">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                </svg>
                País
              </label>
              <select
                id="select-pais"
                value={filtroPais}
                onChange={(e) => handleFiltroPais(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              >
                <option value="todos">Todos los países</option>
                {data.paises.map((pais) => (
                  <option key={pais.id} value={String(pais.id)}>
                    {pais.pais} ({pais.total} médicos)
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro de Especialidad */}
            <div>
              <label htmlFor="select-especialidad" className="flex items-center text-sm font-semibold text-gray-900 mb-2">
                <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
                Especialidad
              </label>
              <select
                id="select-especialidad"
                value={filtroEspecialidad}
                onChange={(e) => setFiltroEspecialidad(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
              >
                <option value="todas">Todas las especialidades</option>
                {computed.espDisponibles.map((esp) => (
                  <option key={esp.especialidad} value={esp.especialidad}>
                    {esp.especialidad} ({esp.total} médicos)
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Estadísticas Generales */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 lg:p-6 border border-blue-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span>{computed.tituloResumen}</span>
          </h2>

          {/* Totales */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">{computed.totalMedicos.toLocaleString('es-EC')}</p>
              <p className="text-sm text-gray-600 mt-1">Total Médicos</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-blue-600">{computed.totalPaises}</p>
              <p className="text-sm text-gray-600 mt-1">Países</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-indigo-600">{computed.totalEspecialidades}</p>
              <p className="text-sm text-gray-600 mt-1">Especialidades</p>
            </div>
          </div>
        </div>

        {/* Desglose por País */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setPaisesExpanded(!paisesExpanded)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-blue-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-base lg:text-lg">Análisis por País</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">({computed.totalPaises} países)</span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${paisesExpanded ? 'rotate-180' : 'animate-pulse'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {paisesExpanded && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">País</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Total</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {computed.paisesArray.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-8 text-center text-gray-500">No hay datos para mostrar</td>
                    </tr>
                  ) : (
                    computed.paisesArray.map((pais) => (
                      <tr key={pais.pais} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{pais.pais}</td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{pais.total}</td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-blue-600">{pais.porcentaje}%</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Desglose por Especialidad */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setEspecialidadesExpanded(!especialidadesExpanded)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-indigo-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-indigo-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
                <span className="text-base lg:text-lg">Análisis por Especialidad</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">({computed.totalEspecialidades} especialidades)</span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${especialidadesExpanded ? 'rotate-180' : 'animate-pulse'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {especialidadesExpanded && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Especialidad</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Total</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {computed.especialidadesArray.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-8 text-center text-gray-500">No hay datos para mostrar</td>
                    </tr>
                  ) : (
                    computed.especialidadesArray.map((esp) => (
                      <tr key={esp.especialidad} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{esp.especialidad}</td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{esp.total}</td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-indigo-600">{esp.porcentaje}%</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 text-center text-gray-600 text-xs lg:text-sm">
          <p>&copy; 2026 Tonal Strategies - Análisis de Base de Datos</p>
        </div>

      </div>
    </div>
  )
}
